import {
  assign,
  createMachine,
  DoneInvokeEvent,
  MachineConfig,
  MachineOptions
} from "xstate4"
import _differenceBy from "lodash/differenceBy"
import { StorageLabel } from "../../../mp-utils/generic-utils"
import storage from "../../../mp-core/helpers/storage"
import {
  ExistingPlanOptions,
  EocStudyPlanSectionType,
  EocStudyPlanActivationInheritMode,
  newPlanInputFormProperties,
  PlanIntent,
  Program
} from "../../types"
import { StudentStudyPlanStatsInput } from "../../../../types/gqlTypes/graphql"
import { Context, Schema, StudyPlanTargets, Transitions } from "./types"
import { NO_SUBJECT_SELECTED_ERROR } from "../constants"

import {
  generateTargetedRefScope,
  removeSectionItemByContentId,
  getCurrentPlanForEachSubjectByRefScopeBySubject,
  toggleItemTeachLive,
  getCurrentPlanWithEventType
} from "../../../../services/ready-services/studyPlan/studyPlanServices"
import {
  getAdminRelevantPlanInfo,
  getDraftPlan,
  getItemPreview,
  getPlanAvailableItems,
  getSectionAvailableItems,
  getAssignedSubjectsByRefScope,
  getTeacherRelevantPlanInfo,
  getRecommendedPlanByRefScope,
  getGlobalPlanSettings,
  getResetClassMasteryLessonsAffectedCount,
  getStudentStudyPlanStats
} from "../../../../services/ready-services/studyPlan/studyPlanQueryResolver"

import {
  newPlan,
  redo,
  undo,
  addSection,
  updateSection,
  removeSection,
  addSectionItem,
  cancelPlan,
  activatePlan,
  reorderSection,
  reorderSectionItem,
  moveItemToNewSection,
  updatePlan,
  updateSectionItem,
  replaceSectionItem,
  changeToAdaptivePlan,
  changeToManualPlan,
  markBreakBeforeSection,
  markBreakAfterSection,
  markBreakBeforeItem,
  markBreakAfterItem,
  clearBreakBeforeSection,
  clearBreakAfterSection,
  clearBreakBeforeItem,
  clearBreakAfterItem,
  clearItemTeachLive,
  switchToRecommendedPlan,
  markItemTeachLive,
  clearItemCompleted,
  markItemCompleted,
  resetMasteryLessons,
  restoreMasteryLessons,
  setGlobalStudentSettings
} from "../../../../services/ready-services/studyPlan/studyPlanMutationResolver"
import {
  studyPlansOverview,
  studyPlansScores,
  confirmOverrideTransition,
  beforeLogin,
  landingView,
  startPlan,
  reviewPlan,
  editPlan,
  teacherView,
  viewBreakDrawerState
} from "./states"
import {
  buildPopulatedStudyPlanTranstition,
  cancelPlanTransition,
  editPublishedPlanTransition,
  buildStudyPlanTransition,
  fetchStudyPlanTransition,
  saveStudyPlanTransition,
  getCurrentPlanTransition,
  getRecommendedPlanTransition,
  restoreToRecommendedPlanTransition,
  removeLessonTransition,
  addLessonTransition,
  replaceLessonTransition,
  addSectionTransition,
  undoTransition,
  redoTransition,
  getLessonPreviewTransition,
  getProgramCoursesTransition
} from "./transitionalStates"
import {
  getPlanRefIdByRefScope,
  getSubjectPath
} from "../../study-plans/helpers/utils"
import { getPlanIntent, getStudyPlanStorageKey } from "./helpers"
import {
  getAvailableContentDurationByProgramCourse,
  getAvailableCoursesByProgram
} from "../../../../services/ready-services/report/reportQueryResolver"

/*
  Note: if you change anything run test in package/ready
  using command: `yarn test:machines`
  and make sure all tests pass
*/

/* // useful ids
##  Olivers District
	* id: `bc6df2f0-a407-11ea-a776-5f957d098c35`

### Schools
#### Pope High School
	* id: `d31eaa30-a407-11ea-a776-5f957d098c35`

### Educators
#### Pope DA
	* id: `32470ec0-a409-11ea-a776-5f957d098c35`
	* role: `District Admin`
	* is_teacher: `false`
#### Pope Principal
	* id: `ff472380-a407-11ea-a776-5f957d098c35`
	* role: `School Admin`
	* is_teacher: `false`
#### Pope Teacher
	* id: `2e2a5000-a408-11ea-a776-5f957d098c35`
	* role: `Teacher`
	* is_teacher: `true`
*/

const initialContext: Context = {
  initialUrlSubject: null,
  availableCourses: [],
  selfRefScope: null,
  currentTarget: "",
  showDefaultInheritance: true,
  showMasterPlanInfo: true,
  redirectUrl: null,
  loading: false,
  studyPlansListBySubjects: null,
  studyPlan: null,
  hasUnsavedStudyPlanChanges: false,
  publishedStudyPlan: null,
  recommendedStudyPlan: null,
  seconds: null,
  maxAvailableContentDuration: null,
  isBlank: null,
  program: "STAAR",
  subject: null,
  isOldNav: true,
  isReadOnly: false,
  editHistory: [],
  teacherAssignedSubjects: null,
  existingPlanOptionsModal: false,
  existingPlanOption: ExistingPlanOptions.NOTSET,
  // Open/close modals
  modifyHoursModal: false,
  addLessonModal: false,
  lessonDetailViewModal: false,
  modifySectionSecondsModal: false,
  planSettingModal: false,
  moveOutOfSectionModal: false,
  confirmConvertingPlanModal: false,
  assignClassModal: false,
  viewBreakDrawer: false,
  firstSavingConfirmModal: false,
  confirmRecommendedRestore: false,
  viewGlobalSettings: false,
  confirmLeaveModal: false,
  openConfirmResetMasteryLesson: false,
  openConfirmRestoreMasteryLesson: false,
  pendingProgramChange: null,
  // Edit settings
  expandAll: false,
  isReorderMode: false,
  expanded: [],
  showLearnMore: true,
  planSettings: null,
  // Students Scores Data
  scoresByStudentData: {
    headers: [],
    students: [],
    pageCount: 0
  },
  // Lesson data from API
  lessonOptions: [],
  lessonDetails: null,
  assignableClasses: [],
  currentClassMasteryLessonAffectedCount: null,

  user: null
}

const config: MachineConfig<Context, Schema, Transitions> = {
  id: "StudyPlan",
  initial: "before_login",
  context: initialContext,
  on: {
    SET_PLAN_REF_SCOPE: {
      actions: assign((_ctx, evt) => ({
        currentPlanScope: evt.scope,
        currentTargetPlanRefId: evt.targetRefId
      }))
    },
    SET_HAS_UNSAVED_CHANGES: {
      actions: assign((_ctx, evt) => ({
        hasUnsavedStudyPlanChanges: evt.value
      }))
    },
    CLEAR_REDIRECT_URL: {
      actions: assign({
        redirectUrl: null
      })
    },
    CLEAR_EXTERNAL_REDIRECT_URL: {
      actions: assign({
        externalRedirectUrl: null
      })
    },
    SET_STATE: [
      {
        // If selfRefScope is not set yet, just update context and stay in current state
        actions: assign<Context, Extract<Transitions, { type: "SET_STATE" }>>(
          (ctx, evt) => {
            return {
              ...(evt.program && {
                program: evt.program.toUpperCase() as Program
              }),
              ...(evt.subject !== undefined && { subject: evt.subject }),
              ...(evt.scope && { currentPlanScope: evt.scope }),
              ...(evt.classId && {
                selectedEntity: {
                  entityId: evt.classId,
                  entityName: evt.className
                }
              }),
              // Store the subject from URL to use later
              ...(evt.subject && { initialUrlSubject: evt.subject })
            }
          }
        ),
        cond: ctx => !ctx.selfRefScope
      },
      {
        // If navigating to scores subpage, go directly to scores state
        target: "#study_plans_scores",
        actions: assign<Context, Extract<Transitions, { type: "SET_STATE" }>>(
          (ctx, evt) => {
            return {
              ...(evt.program && {
                program: evt.program.toUpperCase() as Program
              }),
              ...(evt.subject !== undefined && { subject: evt.subject }),
              ...(evt.scope && { currentPlanScope: evt.scope }),
              ...(evt.classId && {
                selectedEntity: {
                  entityId: evt.classId,
                  entityName: evt.className
                }
              }),
              currentTarget: StudyPlanTargets.Scores
            }
          }
        ),
        cond: (ctx, evt) => {
          // Route directly to scores if:
          // 1. We're on the scores subpage
          // 2. We already have selfRefScope and program data loaded
          // 3. We're not currently loading
          return (
            evt.subpage === "scores" &&
            !!ctx.selfRefScope &&
            !!ctx.program &&
            !ctx.loading
          )
        }
      },
      {
        // If we already have availableCourses for the current program, go directly to idle
        target: "#StudyPlan.get_program_courses.idle",
        actions: assign<Context, Extract<Transitions, { type: "SET_STATE" }>>(
          (ctx, evt) => {
            // Determine the target based on the event data and current user
            let currentTarget: StudyPlanTargets | undefined

            if (evt.subpage === "view" && evt.entityType === "recommended") {
              currentTarget = StudyPlanTargets.ViewRecommended
            } else if (evt.subpage === "view") {
              currentTarget = StudyPlanTargets.ReviewPlan
            } else if (evt.subpage === "edit") {
              currentTarget = StudyPlanTargets.Edit
            } else if (
              ctx.user?.role === "Teacher" &&
              !evt.subpage &&
              !!evt.subject
            ) {
              currentTarget = StudyPlanTargets.TeacherView
            } else if (
              (ctx.user?.role === "District Admin" ||
                ctx.user?.role === "School Admin") &&
              !!evt.subject
            ) {
              currentTarget = StudyPlanTargets.Overview
            } else {
              currentTarget = StudyPlanTargets.Landing
            }

            return {
              ...(evt.program && {
                program: evt.program.toUpperCase() as Program
              }),
              ...(evt.subject !== undefined && { subject: evt.subject }),
              ...(evt.scope && { currentPlanScope: evt.scope }),
              ...(evt.classId && {
                selectedEntity: {
                  entityId: evt.classId,
                  entityName: evt.className
                }
              }),
              currentTarget
            }
          }
        ),
        cond: (ctx, evt) => {
          // Only skip loading if we have selfRefScope, availableCourses AND the program hasn't changed
          const programMatches =
            !!evt.program && evt.program.toUpperCase() === ctx.program
          return (
            !ctx.loading && // Don't skip if we're currently loading
            ctx.selfRefScope &&
            programMatches &&
            ctx.availableCourses &&
            ctx.availableCourses.length > 0
          )
        }
      },
      {
        // Otherwise, go through loading to fetch availableCourses
        target: "#StudyPlan.get_program_courses",
        actions: assign<Context, Extract<Transitions, { type: "SET_STATE" }>>(
          (ctx, evt) => {
            // Determine the target based on the event data and current user
            let currentTarget: StudyPlanTargets | undefined

            if (evt.subpage === "view" && evt.entityType === "recommended") {
              currentTarget = StudyPlanTargets.ViewRecommended
            } else if (evt.subpage === "view") {
              currentTarget = StudyPlanTargets.ReviewPlan
            } else if (evt.subpage === "edit") {
              currentTarget = StudyPlanTargets.Edit
            } else if (
              ctx.user?.role === "Teacher" &&
              !evt.subpage &&
              !!evt.subject
            ) {
              currentTarget = StudyPlanTargets.TeacherView
            } else if (
              (ctx.user?.role === "District Admin" ||
                ctx.user?.role === "School Admin") &&
              !!evt.subject
            ) {
              currentTarget = StudyPlanTargets.Overview
            } else {
              currentTarget = StudyPlanTargets.Landing
            }

            return {
              ...(evt.program && {
                program: evt.program.toUpperCase() as Program
              }),
              ...(evt.subject !== undefined && { subject: evt.subject }),
              ...(evt.scope && { currentPlanScope: evt.scope }),
              ...(evt.classId && {
                selectedEntity: {
                  entityId: evt.classId,
                  entityName: evt.className
                }
              }),
              currentTarget
            }
          }
        )
      }
    ],
    /*
      These (RESET_MASTERY_LESSON and RESTORE_MASTERY_LESSON) are higher level events
      so we can acess them on the snackbar undo button
    */
    RESET_MASTERY_LESSON: {
      target: "#StudyPlan.teacher_view.reset_mastery_lesson_transition",
      cond: (ctx, _evt) => ctx.selfRefScope.scope === "TEACHER"
    },
    RESTORE_MASTERY_LESSON: {
      target: "#StudyPlan.teacher_view.restore_mastery_lesson_transition",
      cond: (ctx, _evt) => ctx.selfRefScope.scope === "TEACHER"
    },
    // When program changes we need to fetch available courses.
    CHANGE_PROGRAM: [
      {
        // If only subject changed, go directly to get_program_courses.idle to determine what state we should land on
        target: "#StudyPlan.get_program_courses.idle",
        actions: assign<
          Context,
          Extract<Transitions, { type: "CHANGE_PROGRAM" }>
        >((_ctx, evt) => {
          return {
            program: evt.program,
            subject: evt.subject ?? null
          }
        }),
        cond: (ctx, evt) =>
          !ctx.loading && // Don't interrupt if we're currently loading
          evt.program === ctx.program &&
          ctx.availableCourses &&
          ctx.availableCourses.length > 0 &&
          evt.subject !== ctx.subject
      },
      {
        // Otherwise, go to get_program_courses (loading)
        target: "#StudyPlan.get_program_courses",
        actions: assign<
          Context,
          Extract<Transitions, { type: "CHANGE_PROGRAM" }>
        >((_ctx, evt) => {
          return {
            program: evt.program,
            subject: evt.subject ?? null
          }
        })
      }
    ]
  },
  states: {
    /* Transitional states */
    /* Overview page transitions */
    get_recommended_plan_info_transition: {
      ...getRecommendedPlanTransition
    },
    restore_to_recommended_plan_transition: {
      ...restoreToRecommendedPlanTransition
    },
    /* Save or cancel transitional states */

    cancel_plan_transition: { ...cancelPlanTransition },
    save_study_plan_transition: { ...saveStudyPlanTransition },
    confirm_override: { ...confirmOverrideTransition },
    /* View or edit mode transitional states */
    build_study_plan_transition: { ...buildStudyPlanTransition },
    build_populated_study_plan_transition: {
      ...buildPopulatedStudyPlanTranstition
    },
    fetch_study_plan_transition: { ...fetchStudyPlanTransition },
    edit_published_plan_transition: { ...editPublishedPlanTransition },

    get_current_plan: { ...getCurrentPlanTransition },
    get_lesson_preview_transition: { ...getLessonPreviewTransition },
    remove_lesson_transition: { ...removeLessonTransition },
    add_lesson_transition: { ...addLessonTransition },
    replace_lesson_transition: { ...replaceLessonTransition },
    add_section_transition: { ...addSectionTransition },
    undo_transition: { ...undoTransition },
    redo_transition: { ...redoTransition },

    /* Main states in order */
    before_login: { ...beforeLogin },
    get_program_courses: { ...getProgramCoursesTransition },
    landing_view: { ...landingView },
    teacher_view: { ...teacherView },
    study_plans_overview: { ...studyPlansOverview },
    study_plans_scores: { ...studyPlansScores },
    start_plan: { ...startPlan },
    review_plan: { ...reviewPlan },
    edit_plan: { ...editPlan },
    view_break: { ...viewBreakDrawerState }
  },
  predictableActionArguments: true,
  preserveActionOrder: true
}

const getOptions = (
  actions = null,
  guards = null,
  services = null
): Partial<MachineOptions<Context, Transitions>> => ({
  guards: {
    checkIfLessonAdded: (_ctx, _evt) => true,
    checkSelectedSection: (ctx, evt: DoneInvokeEvent<any>) => {
      const selectedSection = evt.data.plan.sections.find(
        section => section.id === ctx.selectedSectionId
      )
      return !!selectedSection
    },
    ...guards
  },
  services: {
    getProgramCourses: async ctx => {
      if (!ctx.program) {
        return []
      }

      try {
        const result = await getAvailableCoursesByProgram({
          program: ctx.program
        })
        return result
      } catch (error) {
        console.error("[getProgramCourses service] Error:", error)
        throw error
      }
    },
    getStudyPlansBySubject: async (ctx, evt) => {
      return getCurrentPlanForEachSubjectByRefScopeBySubject(
        ctx.program,
        ctx.selfRefScope
      )
    },
    buildStudyPlan: async (ctx, evt) => {
      if (evt.type !== "START_BLANK_STUDY_PLAN") {
        throw new Error(
          "buildStudyPlan with unexpected event type. type:" + evt.type
        )
      }
      if (!ctx.program) console.error("Building a plan without a program")
      if (!ctx.subject) console.error("Building a plan without a subject")

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      const properties: newPlanInputFormProperties = {
        targetSeconds: ctx.seconds ?? 0,
        intent: PlanIntent.NEW,
        start_populated: !ctx.isBlank,
        sub_type: evt.subType
      }
      return newPlan(ctx.program, ctx.subject, refScope, properties)
    },

    buildPopulatedStudyPlan: async (ctx, evt) => {
      // evt.type === "" means that this was triggered by xState event and not a user action
      if (evt.type !== "BUILD_STUDY_PLAN" && evt.type !== "") {
        throw new Error(
          "buildStudyPlan with unexpected event type. type:" + evt.type
        )
      }

      if (!ctx.program) console.error("Building a plan without a program")
      if (!ctx.subject) console.error("Building a plan without a subject")

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      const planIntent = getPlanIntent(
        ctx.publishedStudyPlan,
        ctx.selectedEntity,
        ctx.selfRefScope.scope
      )

      const properties: newPlanInputFormProperties = {
        start_populated: true,
        intent: evt.intent ?? planIntent,
        sub_type: evt.subType,
        ...(ctx.seconds && { targetSeconds: ctx.seconds })
      }
      return newPlan(ctx.program, ctx.subject, refScope, properties)
    },
    fetchDraftStudyPlan: async (ctx, evt) => {
      if (evt.type !== "EDIT") {
        throw new Error("addSection with invalid event type: " + evt.type)
      }
      // TODO: fetchDraftStudyPlan never get called in xState - create a test
      //TODO: check if it's a draft plan or published plan
      const passedIdOrCurrentId = ctx.selectedPlanId ?? ctx.studyPlan.id
      return getDraftPlan(passedIdOrCurrentId)
    },
    editPlanDuration: async (ctx, evt) => {
      if (evt.type !== "BUILD_STUDY_PLAN") {
        throw new Error("editPlanDuration with invalid event type: " + evt.type)
      }

      return updatePlan(ctx.studyPlan.id, {
        targetSeconds: ctx.seconds ?? 30
      })
    },
    redo: async (ctx, evt) => {
      if (evt.type !== "REDO") {
        throw new Error("redo with unexpected event type: " + evt.type)
      }
      return redo(ctx.studyPlan.id)
    },
    undo: async (ctx, evt) => {
      if (evt.type !== "UNDO") {
        throw new Error("undo with unexpected event type: " + evt.type)
      }
      return undo(ctx.studyPlan.id)
    },

    addSection: async (ctx, evt) => {
      let section_type: EocStudyPlanSectionType | "" = ""

      if (
        evt.type !== "ADD_BOOT_CAMP_SECTION" &&
        evt.type !== "ADD_BREAK_SECTION" &&
        evt.type !== "ADD_PRACTICE_TEST_SECTION" &&
        evt.type !== "ADD_MASTERY_LESSON_SECTION"
      ) {
        throw new Error("addSection with invalid section type: " + evt.type)
      }

      switch (evt.type) {
        case "ADD_BOOT_CAMP_SECTION":
          section_type = "BOOT_CAMP"
          break
        case "ADD_BREAK_SECTION":
          section_type = "BREAK"
          break
        case "ADD_PRACTICE_TEST_SECTION":
          section_type = "PRACTICE_TEST"
          break
        case "ADD_MASTERY_LESSON_SECTION":
          section_type = "MASTERY_LESSON"
          break
      }
      return addSection(
        ctx.studyPlan.id,
        section_type,
        evt?.targetSeconds ?? null
      )
    },
    updateSectionSecond: async (ctx, evt) => {
      if (evt.type !== "SAVE_SECTION_SECOND") {
        throw new Error(
          "updateSectionSecond with unexpected event type: " + evt.type
        )
      }
      if (ctx.selectedSectionId === null) {
        throw new Error("updateSectionSecond without ctx.selectedSectionId")
      }
      return updateSection(
        ctx.studyPlan.id,
        ctx.selectedSectionId, // assume it is selected
        //ctx.targetSectionSeconds // selected
        { targetSeconds: ctx.targetSectionSeconds }
      )
    },
    removeSection: async (ctx, evt) => {
      if (evt.type === "CLOSE" && ctx.selectedSectionId) {
        return removeSection(ctx.studyPlan.id, ctx.selectedSectionId)
      }

      if (evt.type !== "REMOVE_SECTION") {
        throw new Error("removeSection with unexpected event type: " + evt.type)
      }
      return removeSection(ctx.studyPlan.id, evt.sectionId)
    },
    addSectionItem: async (ctx, evt) => {
      if (evt.type !== "ADD_LESSON") {
        throw new Error(
          "addSectionItem with unexpected event type: " + evt.type
        )
      }

      return addSectionItem(
        ctx.studyPlan.id,
        ctx.selectedSectionId, // evt. sectionId
        evt.itemContentId,
        evt.itemProperties ?? {}
      )
    },
    replaceSectionItem: async (ctx, evt) => {
      if (evt.type !== "REPLACE_LESSON") {
        throw new Error(
          "replaceSectionItem with unexpected event type: " + evt.type
        )
      }
      if (
        ctx.studyPlan.sections.find(s => s.id === ctx.selectedSectionId)
          ?.type != "PRACTICE_TEST"
      ) {
        throw new Error(
          "replaceSectionItem on non Practice Test section is not allowed"
        )
      }

      return replaceSectionItem(
        ctx.studyPlan.id,
        ctx.selectedSectionId, // evt. sectionId
        evt.itemContentId,
        evt.itemProperties ?? {}
      )
    },
    // short cut for update section item's is_teach_live property.
    toggleItemTeachLive: async (ctx, evt) => {
      if (evt.type !== "TEACH_LIVE") {
        throw new Error(
          "updateSectionItemService with unexpected event type: " + evt.type
        )
      }
      const sectionId = evt.sectionId ?? ctx.selectedSectionId
      const lessonId = evt.lessonId ?? ctx.selectedLessonId

      const item = ctx.studyPlan.sections
        .find(s => s.id === sectionId)
        .items.find(i => i.contentId === lessonId)
      return toggleItemTeachLive(ctx.studyPlan.id, sectionId, item)
    },
    toggleBreakBeforeSection: async (ctx, evt) => {
      if (evt.type !== "TOGGLE_BREAK_BEFORE_SECTION") {
        throw new Error(
          "toggleBreakBeforeSection with unexpected event type: " + evt.type
        )
      }
      const sectionId = evt?.sectionId ?? ctx.selectedSectionId

      const section = ctx.studyPlan.sections.find(s => s.id === sectionId)
      const breakStatus = !(section?.breakBefore ?? false)
      const result = await updateSection(ctx.studyPlan.id, sectionId, {
        breakBefore: breakStatus
      })
      return { result, breakStatus }
    },
    toggleBreakAfterSection: async (ctx, evt) => {
      if (evt.type !== "TOGGLE_BREAK_AFTER_SECTION") {
        throw new Error(
          "toggleBreakAfterSection with unexpected event type: " + evt.type
        )
      }
      const sectionId = evt?.sectionId ?? ctx.selectedSectionId

      const section = ctx.studyPlan.sections.find(s => s.id === sectionId)
      const breakStatus = !(section?.breakAfter ?? false)
      const result = await updateSection(ctx.studyPlan.id, sectionId, {
        breakAfter: breakStatus
      })
      return { result, breakStatus }
    },
    toggleBreakBeforeLesson: async (ctx, evt) => {
      if (evt.type !== "TOGGLE_BREAK_BEFORE_LESSON") {
        throw new Error(
          "toggleBreakBeforeLesson with unexpected event type: " + evt.type
        )
      }
      const sectionId = evt?.sectionId ?? ctx.selectedSectionId
      const lessonId = evt?.lessonId ?? ctx.selectedLessonId

      const item = ctx.studyPlan.sections
        .find(s => s.id === sectionId)
        .items.find(i => i.id === lessonId)
      const breakStatus = !(item?.breakBefore ?? false)
      const result = await updateSectionItem(
        ctx.studyPlan.id,
        sectionId,
        lessonId,
        {
          breakBefore: breakStatus
        }
      )
      return { result, breakStatus }
    },
    toggleBreakAfterLesson: async (ctx, evt) => {
      if (evt.type !== "TOGGLE_BREAK_AFTER_LESSON") {
        throw new Error(
          "toggleBreakAfterLesson with unexpected event type: " + evt.type
        )
      }
      const sectionId = evt?.sectionId ?? ctx.selectedSectionId
      const lessonId = evt?.lessonId ?? ctx.selectedLessonId

      const item = ctx.studyPlan.sections
        .find(s => s.id === sectionId)
        .items.find(i => i.id === lessonId)
      const breakStatus = !(item?.breakAfter ?? false)
      const result = await updateSectionItem(
        ctx.studyPlan.id,
        sectionId,
        lessonId,
        {
          breakAfter: breakStatus
        }
      )
      return { result, breakStatus }
    },
    restoreToRecommended: async (ctx, _evt) => {
      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )
      return switchToRecommendedPlan(ctx.program, ctx.subject, refScope)
    },
    changeMicroSnapsCount: async (ctx, evt) => {
      if (evt.type !== "CHANGE_MICROSNAPS_COUNT") {
        throw new Error(
          "changeMicroSnapsCount with unexpected event type: " + evt.type
        )
      }

      const selectedSection = ctx.studyPlan?.sections?.find(
        section => section.id === ctx.selectedSectionId
      )
      const selectedItem = selectedSection?.items?.find(
        item => item.contentId === evt.lessonId
      )

      if (!selectedItem) {
        throw new Error("Item is not in section")
      }

      return updateSectionItem(
        ctx.studyPlan.id,
        ctx.selectedSectionId,
        selectedItem.id,
        { selectedMicrosnapCount: evt.count }
      )
    },

    updateSectionItemService: async (ctx, evt) => {
      //console.log("Needs to finish implementaiton")
      //   // TODO: What is this event type?,
      //   return updateSectionItem(
      //     ctx.studyPlan.id,
      //     ctx.selectedSectionId,
      //     evt.lessonId,
      //     evt.itemProperties ?? {}
      //   )
    },

    removeSectionItemByContentId: async (ctx, evt) => {
      if (evt.type !== "REMOVE_LESSON") {
        throw new Error(
          "removeSectionItem with unexpected event type: " + evt.type
        )
      }
      // If source section is empty, it's removed automatically.
      return removeSectionItemByContentId(
        ctx.studyPlan,
        evt.sectionId, // this can be done without selectedSectionId...
        evt.itemContentId
      )
    },
    cancelPlan: async (ctx, evt) => {
      if (evt.type !== "CANCEL" && evt.type !== "CONFIRM") {
        throw new Error("cancelPlan with unexpected event type: " + evt.type)
      }
      return cancelPlan(ctx.studyPlan.id)
    },

    activatePlan: async (ctx, evt) => {
      if (evt.type !== "SAVE") {
        throw new Error("activatePlan with unexpected event type: " + evt.type)
      }
      let overrideMode: EocStudyPlanActivationInheritMode = null
      switch (ctx.existingPlanOption) {
        case ExistingPlanOptions.NO_OVERRIDE:
          overrideMode = "JUST_LEAVE_ALONE"
          break
        case ExistingPlanOptions.OVERRIDE:
          overrideMode = "FORCE_OVERWRITE"
          break
        default:
          overrideMode = "NOTSET"
      }
      const status = await activatePlan(ctx.studyPlan.id, overrideMode)
      //console.log("status: " + JSON.stringify(status))
      if (status.success === true) {
        return
      } else {
        throw "ActivatePlan fail"
      }
    },
    reorderSection: async (ctx, evt) => {
      //console.log("==\n==\n" + JSON.stringify(evt) + "\n==\n==\n")
      if (evt.type !== "DRAG_DROP_SECTION") {
        throw new Error(
          "reorderSection with unexpected event type: " + evt.type
        )
      }

      return reorderSection(
        ctx.studyPlan.id,
        evt.sectionId,
        evt.index // index
      )
    },
    reorderSectionItem: async (ctx, evt) => {
      if (evt.type !== "DRAG_DROP_ITEM") {
        throw new Error(
          "reorderSectionItem with unexpected event type: " + evt.type
        )
      }

      // If source section is empty, it's removed automatically.
      return reorderSectionItem(
        ctx.studyPlan.id,
        evt.sectionId,
        evt.lessonId,
        evt.index, // index
        evt.toSectionId
      )
    },
    moveItemToNewSection: async (ctx, evt) => {
      if (evt.type !== "DRAG_DROP_MOVE_OUT_SECTION_CONFIRM") {
        throw new Error(
          "moveItemToNewSection with unexpected event type: " + evt.type
        )
      }

      // REVIEW: Restrict creating new section if source has only one item?
      return moveItemToNewSection(
        ctx.studyPlan.id,
        ctx.selectedSectionId,
        ctx.selectedLessonId,
        ctx.studyPlan.sections.length // if not provided, go to last
      )
    },
    getPlanAvailableItems: async ctx => {
      return getPlanAvailableItems(
        ctx.studyPlan?.id ?? ctx.publishedStudyPlan?.id ?? ""
      )
    },
    getLessons: async (ctx, _evt) => {
      return getSectionAvailableItems(
        ctx.studyPlan?.id ?? "",
        ctx.selectedSectionId
      )
    },
    getLessonDetails: async (ctx, _evt) => {
      // Published manual plan for teacher.
      if (ctx.publishedStudyPlan?.subType === "MANUAL") {
        return getItemPreview(
          ctx.selectedLessonId,
          ctx.publishedStudyPlan.id,
          ctx.selectedSectionId
        )
      }

      // Published or draft adaptive plan.
      const targetPlan = ctx.studyPlan ?? ctx.publishedStudyPlan

      if (targetPlan?.subType === "ADAPTIVE") {
        return getItemPreview(ctx.selectedLessonId, ctx.publishedStudyPlan.id)
      }

      // For draft plans, pass sectionId and planId.
      return getItemPreview(
        ctx.selectedLessonId,
        ctx.studyPlan.id,
        ctx.selectedSectionId
      )
    },
    // NOTE: will use after modifications of getPlanHistory
    // getEditHistory: async (ctx, _evt) => {
    //   const refScope = generateTargetedRefScope(
    //     ctx.selfRefScope,
    //     ctx.currentPlanScope,
    //     ctx.currentTargetPlanRefId
    //   )

    //   return getPlanHistory(ctx.program, ctx.subject, refScope)
    // },
    getAdminRelevantPlanInfo: async (ctx, _evt) => {
      return getAdminRelevantPlanInfo(
        ctx.program,
        ctx.subject,
        ctx.selfRefScope
      )
    },
    getTeacherRelevantPlanInfo: async (ctx, _evt) => {
      return getTeacherRelevantPlanInfo(
        ctx.program,
        ctx.subject,
        ctx.selfRefScope
      )
    },
    getCurrentPlan: async (ctx, evt) => {
      if (evt.type === "FETCH_PLAN_BY_REF_SCOPE") {
        const { entityId, scope } = evt

        const refScope = generateTargetedRefScope(
          ctx.selfRefScope,
          scope ?? ctx.selfRefScope.scope,
          entityId || getPlanRefIdByRefScope(ctx.selfRefScope)
        )

        return getCurrentPlanWithEventType(
          ctx.program,
          ctx.subject,
          refScope,
          true,
          evt
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      /* Get the user's current plan */
      return getCurrentPlanWithEventType(
        ctx.program,
        ctx.subject,
        refScope,
        true,
        evt
      )
    },
    getRecommendedPlan: async (ctx, evt) => {
      if (evt.type !== "VIEW_RECOMMENDED_PLAN" && evt.type !== "SYNC") {
        throw new Error(
          "getRecommendedPlan with unexpected event type: " + evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      return getRecommendedPlanByRefScope(ctx.program, ctx.subject, refScope)
    },
    restoreToRecommendedPlan: async (ctx, evt) => {
      if (evt.type !== "RESTORE_PLAN") {
        throw new Error(
          "restoreToRecommendedPlan with unexpected event type: " + evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      return switchToRecommendedPlan(ctx.program, ctx.subject, refScope)
    },
    getLessonPreview: (ctx, evt) => {
      if (evt.type !== "PREVIEW") {
        throw new Error(
          "getLessonPreview with unexpected event type: " + evt.type
        )
      }
      // if providing evt.contentId, use it.
      // otherwise, try to find from evt.lessonId or ctx.selectedLessonId
      let contentId = evt.contentId
      const planId = evt?.planId ?? ctx.selectedPlanId ?? null
      const sectionId = evt?.sectionId ?? ctx.selectedSectionId ?? null

      if (!contentId) {
        const section = ctx.studyPlan.sections.find(
          s => s.items.findIndex(i => i.id === evt.lessonId) >= 0
        )
        if (section) {
          contentId = section.items.find(i => i.id === evt.lessonId)?.contentId
        }
      }
      if (!contentId) {
        const section = ctx.studyPlan.sections.find(
          s => s.items.findIndex(i => i.id === ctx.selectedLessonId) >= 0
        )
        if (section) {
          contentId = section.items?.find(
            i => i.id === ctx.selectedLessonId
          )?.contentId
        }
      }
      if (!contentId) {
        throw new Error("getLessonPreview cannot figure out contentId")
      }
      return getItemPreview(contentId, planId, sectionId)
    },
    downloadResources: async (ctx, evt) => {},
    changeToAdaptivePlan: async (ctx, evt) => {
      if (evt.type !== "CONVERT_TO_ADAPTIVE") {
        throw new Error(
          "changeToAdaptivePlan with unexpected event type: " + evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      return changeToAdaptivePlan(ctx.studyPlan?.id, refScope)
    },
    changeToManualPlan: async (ctx, evt) => {
      if (evt.type !== "CONVERT_TO_MANUAL") {
        throw new Error(
          "changeToAdaptivePlan with unexpected event type: " + evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      return changeToManualPlan(ctx.studyPlan?.id, refScope)
    },
    getAssignedSubjectsByRefScope: async (ctx, _evt) => {
      return getAssignedSubjectsByRefScope(ctx.program, ctx.selfRefScope)
    },
    togglePublishedPlanBreakBeforeSection: async (ctx, evt) => {
      if (evt.type !== "TOGGLE_BREAK_BEFORE_SECTION") {
        throw new Error(
          "togglePublishedPlanBeforeSectionBreak with unexpected event type: " +
            evt.type
        )
      }

      const section = ctx.publishedStudyPlan.sections.find(
        section => section.id === evt.sectionId
      )

      if (!section) {
        throw new Error(
          "togglePublishedPlanBeforeSectionBreak section not found"
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      if (section.breakBefore) {
        return clearBreakBeforeSection(
          ctx.program,
          ctx.subject,
          refScope,
          ctx.publishedStudyPlan.plan_guid,
          section.section_guid
        )
      }

      return markBreakBeforeSection(
        ctx.program,
        ctx.subject,
        refScope,
        ctx.publishedStudyPlan.plan_guid,
        section.section_guid
      )
    },
    togglePublishedPlanBreakAfterSection: async (ctx, evt) => {
      if (evt.type !== "TOGGLE_BREAK_AFTER_SECTION") {
        throw new Error(
          "togglePublishedPlanAfterSectionBreak with unexpected event type: " +
            evt.type
        )
      }

      const section = ctx.publishedStudyPlan.sections.find(
        section => section.id === evt.sectionId
      )

      if (!section) {
        throw new Error(
          "togglePublishedPlanAfterSectionBreak section not found"
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      if (section.breakAfter) {
        return clearBreakAfterSection(
          ctx.program,
          ctx.subject,
          refScope,
          ctx.publishedStudyPlan.plan_guid,
          section.section_guid
        )
      }

      return markBreakAfterSection(
        ctx.program,
        ctx.subject,
        refScope,
        ctx.publishedStudyPlan.plan_guid,
        section.section_guid
      )
    },
    togglePublishedPlanBreakBeforeItem: async (ctx, evt) => {
      if (evt.type !== "TOGGLE_BREAK_BEFORE_LESSON") {
        throw new Error(
          "togglePublishedPlanBreakBeforeItem with unexpected event type: " +
            evt.type
        )
      }

      const item = ctx.publishedStudyPlan.sections
        .find(section => section.id === evt.sectionId)
        ?.items?.find(item => item.contentId === evt.lessonId)

      if (!item) {
        throw new Error("togglePublishedPlanBeforeSectionBreak item not found")
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      if (item.breakBefore) {
        return clearBreakBeforeItem(
          ctx.program,
          ctx.subject,
          refScope,
          ctx.publishedStudyPlan.plan_guid,
          item.item_guid
        )
      }

      return markBreakBeforeItem(
        ctx.program,
        ctx.subject,
        refScope,
        ctx.publishedStudyPlan.plan_guid,
        item.item_guid
      )
    },
    togglePublishedPlanBreakAfterItem: async (ctx, evt) => {
      if (evt.type !== "TOGGLE_BREAK_AFTER_LESSON") {
        throw new Error(
          "togglePublishedPlanBreakAfterItem with unexpected event type: " +
            evt.type
        )
      }

      const item = ctx.publishedStudyPlan.sections
        .find(section => section.id === evt.sectionId)
        ?.items?.find(item => item.contentId === evt.lessonId)

      if (!item) {
        throw new Error("togglePublishedPlanBreakAfterItem item not found")
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      if (item.breakAfter) {
        return clearBreakAfterItem(
          ctx.program,
          ctx.subject,
          refScope,
          ctx.publishedStudyPlan.plan_guid,
          item.item_guid
        )
      }

      return markBreakAfterItem(
        ctx.program,
        ctx.subject,
        refScope,
        ctx.publishedStudyPlan.plan_guid,
        item.item_guid
      )
    },
    togglePublishedTeachLive: async (ctx, evt) => {
      if (evt.type !== "TEACH_LIVE") {
        throw new Error(
          "togglePublishedTeachLive with unexpected event type" + evt.type
        )
      }

      const item = ctx.publishedStudyPlan?.sections
        ?.find(section => section.id === evt.sectionId)
        ?.items?.find(item => item.contentId === evt.lessonId)

      if (!item) {
        throw new Error("togglePublishedTeachLive item not found")
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      if (item.teachLive) {
        return clearItemTeachLive(
          ctx.program,
          ctx.subject,
          refScope,
          ctx.publishedStudyPlan.plan_guid,
          item.item_guid
        )
      }

      return markItemTeachLive(
        ctx.program,
        ctx.subject,
        refScope,
        ctx.publishedStudyPlan.plan_guid,
        item.item_guid
      )
    },
    togglePublishedMarkComplete: async (ctx, evt) => {
      if (evt.type !== "MARK_COMPLETE") {
        throw new Error(
          "togglePublishedMarkComplete with unexpected event type: " + evt.type
        )
      }

      const selectedItem = ctx.publishedStudyPlan.sections
        .find(section => section.id === evt.sectionId)
        ?.items.find(item => item.contentId === evt.lessonId)

      if (!selectedItem) {
        throw new Error("togglePublishedMarkComplete item not found")
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      if (selectedItem.completed) {
        return clearItemCompleted(
          ctx.program,
          ctx.subject,
          refScope,
          ctx.publishedStudyPlan.plan_guid,
          selectedItem.item_guid
        )
      }

      return markItemCompleted(
        ctx.program,
        ctx.subject,
        refScope,
        ctx.publishedStudyPlan.plan_guid,
        selectedItem.item_guid
      )
    },
    toggleDraftMarkComplete: async (ctx, evt) => {
      if (evt.type !== "MARK_COMPLETE") {
        throw new Error(
          "toggleDraftMarkComplete with unexpected event type: " + evt.type
        )
      }

      const sectionId = evt?.sectionId ?? ctx.selectedSectionId
      const lessonId = evt?.lessonId ?? ctx.selectedLessonId

      const selectedItem = ctx.studyPlan?.sections
        ?.find(s => s.id === sectionId)
        ?.items.find(i => i.contentId === lessonId)

      if (!selectedItem) {
        throw new Error("toggleDraftMarkComplete item not found")
      }

      return updateSectionItem(ctx.studyPlan.id, sectionId, selectedItem.id, {
        completed: !selectedItem.completed
      })
    },
    getGlobalSettings: async (ctx, evt) => {
      if (
        evt.type !== "GET_GLOBAL_SETTINGS" &&
        evt.type !== "done.invoke.getStudyPlansBySubject" &&
        !evt.type.includes("save_global_plan_settings")
      ) {
        throw new Error(
          "getGlobalSettings with unexpected event type" + evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      return await getGlobalPlanSettings({
        program: ctx.program,
        refScope
      })
    },
    getScoresByStudent: async (
      ctx,
      evt: Extract<Transitions, { type: "GET_SCORES_BY_STUDENT" }>
    ) => {
      // Extract parameters from event (if provided) for pagination, search, etc.
      const currentPage = evt.currentPage ?? 1
      const pageSize = evt.pageSize ?? 10

      // Default to the first available course when no subject is selected (API requires one).
      const subject = ctx.subject || ctx.availableCourses?.[0]?.name
      if (!subject) {
        throw new Error(NO_SUBJECT_SELECTED_ERROR)
      }

      // Build the input based on refScope
      const data: StudentStudyPlanStatsInput = {
        program: ctx.program,
        subject,
        page: currentPage,
        pageSize
      }

      // Map refScope to the appropriate arrays
      if (ctx.selfRefScope?.district_id) {
        data.districts = [ctx.selfRefScope.district_id]
      }
      if (
        (ctx.user.role === "School Admin" || ctx.user.role === "Teacher") &&
        ctx.selfRefScope?.school_id
      ) {
        data.schools = [ctx.selfRefScope.school_id]
      }
      // Note: searchText and sort functionality will be added when backend supports it

      const response = await getStudentStudyPlanStats(data)

      return {
        headers: response.headers || [],
        students: response.students || [],
        pageCount: response.pageCount || 1
      }
    },
    downloadScoresByStudent: async () => {
      return {
        snackbar: {
          message: "Download functionality coming soon",
          withUndo: false,
          error: false,
          variant: "info"
        }
      }
    },
    saveDraftPlanSettings: async (ctx, evt) => {
      if (evt.type !== "SAVE_DRAFT_PLAN_SETTINGS") {
        throw new Error(
          "saveDraftPlanSettings with unexpected event type" + evt.type
        )
      }

      const {
        studentCanDoOutOfOrder,
        studentCanPauseTests,
        studentCanResetMasteryLessons,
        studentTestTimingValue,
        studentTestTimingType
      } = evt.planSettings

      return updatePlan(ctx.studyPlan.id, {
        student_can_do_out_of_order: studentCanDoOutOfOrder,
        studentCanPauseTests,
        student_can_reset_mastery_lessons: studentCanResetMasteryLessons,
        studentTestTimingValue,
        studentTestTimingType
      })
    },

    saveGlobalPlanSettings: async (ctx, evt) => {
      if (evt.type !== "SAVE_GLOBAL_PLAN_SETTINGS") {
        throw new Error(
          "saveGlobalPlanSettings with unexpected event type" + evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      const { timingOption, ...restSettings } = evt.globalPlanSettings

      await setGlobalStudentSettings({
        program: ctx.program,
        refScope,
        ...restSettings
      })
    },
    getResetClassMasteryLessonsAffectedCount: async (ctx, evt) => {
      if (
        evt.type !== "OPEN_RESET_MASTERY_LESSON_CONFIRMATION" &&
        evt.type !== "OPEN_RESTORE_MASTERY_LESSON_CONFIRMATION"
      ) {
        throw new Error(
          "get reset class mastery lessons affected count with unexpected event type" +
            evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      if (refScope.scope !== "CLASS") {
        // get affected students count is possible only for CLASS filter
        return null
      }

      return getResetClassMasteryLessonsAffectedCount({
        program: ctx.program,
        refScope,
        contentId: ctx.selectedLessonId,
        subject: ctx.subject
      })
    },
    resetMasteryLesson: async (ctx, evt) => {
      if (evt.type !== "RESET_MASTERY_LESSON") {
        throw new Error(
          "resetMasteryLesson with unexpected event type" + evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      return resetMasteryLessons(
        {
          program: ctx.program,
          refScope,
          contentId: evt.contentId || ctx.selectedLessonId,
          subject: ctx.subject
        },
        evt.allowUndo
      )
    },
    restoreMasteryLesson: async (ctx, evt) => {
      if (evt.type !== "RESTORE_MASTERY_LESSON") {
        throw new Error(
          "resetMasteryLesson with unexpected event type" + evt.type
        )
      }

      const refScope = generateTargetedRefScope(
        ctx.selfRefScope,
        ctx.currentPlanScope,
        ctx.currentTargetPlanRefId
      )

      return restoreMasteryLessons(
        {
          program: ctx.program,
          refScope,
          contentId: evt.contentId || ctx.selectedLessonId,
          subject: ctx.subject
        },
        evt.allowUndo
      )
    },
    getMaxAvailableContentDuration: async (ctx, evt) => {
      if (!ctx.studyPlan) {
        throw new Error(
          "getMaxAvailableContentDuration with unexpected event type" + evt.type
        )
      }
      const { program, subject } = ctx.studyPlan

      return getAvailableContentDurationByProgramCourse({
        program: program,
        course: subject
      })
    },
    ...services
  },
  actions: {
    /* edit_plan.idle - may be in multiple states */
    openNewTabToPreviewLesson: async (ctx, evt: any) => {
      const { ref_id, type } = evt.data ?? {}

      if (!ctx.subject) {
        throw new Error("No subject selected")
      }

      const subjectPath = getSubjectPath(ctx.subject)

      if (ctx.isOldNav) {
        window.open(
          `eoc/study-plans/${subjectPath}/preview?ref_id=${ref_id}&type=${type}`,
          "_blank"
        )
      } else {
        window.open(
          `/reporting/teaching-tools/study-plan/preview?ref_id=${ref_id}&type=${type}`,
          "_blank"
        )
      }
    },

    /* edit_plan.add_lesson */
    filterLessonOptions: async (ctx, evt) => {
      // return lesson optins filtered by user
      // should use ctx to get
    },
    sortLessonOptions: async (ctx, evt) => {},
    rememberDismiss: ctx => {
      storage.set(
        getStudyPlanStorageKey(StorageLabel.SP_LANDING_HELP, ctx.user.id),
        true
      )
    },
    rememberDefaultInheritanceDismiss: ctx => {
      storage.set(
        getStudyPlanStorageKey(
          StorageLabel.SP_DEFAULT_INHERITANCE,
          ctx.user.id
        ),
        true
      )
    },
    rememberDismissMasterPlanInfo: ctx => {
      storage.set(
        getStudyPlanStorageKey(
          StorageLabel.SP_MASTER_PLAN_INFO_VIEW,
          ctx.user.id
        ),
        true
      )
    },
    expandRelatedSections: assign<Context, DoneInvokeEvent<any>>((ctx, evt) => {
      const changedSectionsIds = evt.data.plan.sections
        .filter(section => {
          const prevSection = ctx.studyPlan?.sections.find(
            s => s.id === section.id
          )

          if (!prevSection?.items?.length) {
            return false
          }

          const removedItems = _differenceBy(
            prevSection.items,
            section.items,
            "id"
          )
          const addedItems = _differenceBy(
            section.items,
            prevSection.items,
            "id"
          )
          return !![...removedItems, ...addedItems].length
        })
        .map(section => section.id)

      return {
        // Expand sections that have content changed.
        expanded: Array.from(new Set([...ctx.expanded, ...changedSectionsIds]))
      }
    }),
    onRemoveLesson: assign<Context, DoneInvokeEvent<any>>((ctx, evt) => {
      const prevSectionItems = ctx.studyPlan?.sections?.find(
        section => section.id === ctx.selectedSectionId
      )?.items
      const currentSectionItems = evt.data.plan?.sections?.find(
        section => section.id === ctx.selectedSectionId
      )?.items
      const removedLesson = prevSectionItems?.find(
        lesson =>
          !currentSectionItems?.find(prevLesson => prevLesson.id === lesson.id)
      )

      return {
        lessonDetails: null,
        lessonDetailViewModal: false,
        selectedLessonId: null,
        ...(!ctx.addLessonModal && { selectedSectionId: null }),
        studyPlan: evt.data.plan,
        lessonOptions: evt.data.sectionAvailableItems ?? [],
        snackbar: {
          message: `${removedLesson?.name || "Lesson"} removed`,
          withUndo: true
        }
      }
    }),
    onAddLesson: assign((ctx, evt: DoneInvokeEvent<any>) => {
      const prevSectionItems = ctx.studyPlan?.sections?.find(
        section => section.id === ctx.selectedSectionId
      )?.items
      const currentSectionItems = evt.data.plan?.sections?.find(
        section => section.id === ctx.selectedSectionId
      )?.items
      const addedLesson = currentSectionItems?.find(
        lesson =>
          !prevSectionItems?.find(prevLesson => prevLesson.id === lesson.id)
      )

      return {
        studyPlan: evt.data.plan,
        lessonOptions: evt.data.sectionAvailableItems ?? [],
        snackbar: {
          message: `${addedLesson?.name || "Lesson"} added`,
          withUndo: true
        }
      }
    }),
    // updateItemSeconds become a service.
    /* edit_plan.lesson_detail_view */
    ...actions
  }
})

const createStudyPlanMachine = (
  actions = null,
  guards = null,
  services = null
) => {
  const options = getOptions(actions, guards, services)
  return createMachine<Context, Transitions>(config, options)
}

export default createStudyPlanMachine
